import { Injectable } from '@angular/core';
import type {
  IGenerateTeamOverviewRequest,
  IGenerateTeamOverviewResponse,
  IImportFromWebsiteRequest,
  IImportFromWebsiteResponse,
} from '@principle-theorem/principle-core/interfaces';
import {
  httpsCallable,
  isObject,
  serialise,
  snapshot,
  TypeGuard,
  unserialise,
  type SerialisedData,
} from '@principle-theorem/shared';
import { isString } from 'lodash';
import { duration } from 'moment-timezone';

/**
 * The callable failures the AI Receptionist editor shows different copy for,
 * keeping Firebase's own code values. Any other failure, including one that
 * is not a Firebase error, is `Unknown`.
 */
export enum AiReceptionistCallableErrorCode {
  PermissionDenied = 'permission-denied',
  FailedPrecondition = 'failed-precondition',
  InvalidArgument = 'invalid-argument',
  Unknown = 'unknown',
}

// The Firebase client reports callable codes as 'functions/<code>'
const functionsErrorCodePrefix = /^functions\//;
const fallbackMessage = 'AI Receptionist request failed';
// 10 s past the function's 300 s limit, so the server's own timeout surfaces
const importFromWebsiteTimeoutMs = duration(5, 'minutes')
  .add(10, 'seconds')
  .asMilliseconds();
const generateTeamOverviewTimeoutMs = duration(2, 'minutes').asMilliseconds();

/**
 * A failed AI Receptionist callable, reduced to the codes the editor tells
 * apart.
 */
export class AiReceptionistCallableError extends Error {
  readonly code: AiReceptionistCallableErrorCode;

  constructor(
    code: AiReceptionistCallableErrorCode,
    message: string,
    options?: ErrorOptions
  ) {
    super(message, options);
    this.name = 'AiReceptionistCallableError';
    this.code = code;
  }

  /**
   * Turns whatever a callable rejected with into this error, so callers need
   * a single catch branch. A Firebase error arrives as `functions/<code>`; the
   * original rejection is kept as the `cause` for debugging.
   */
  static from(error: unknown): AiReceptionistCallableError {
    return new AiReceptionistCallableError(
      AiReceptionistCallableError._code(error),
      AiReceptionistCallableError._message(error),
      { cause: error }
    );
  }

  private static _code(error: unknown): AiReceptionistCallableErrorCode {
    if (!isObject(error)) {
      return AiReceptionistCallableErrorCode.Unknown;
    }
    const firebaseCode = error['code'];
    if (!isString(firebaseCode)) {
      return AiReceptionistCallableErrorCode.Unknown;
    }
    const code = firebaseCode.replace(functionsErrorCodePrefix, '');
    return TypeGuard.enumValue(AiReceptionistCallableErrorCode)(code)
      ? code
      : AiReceptionistCallableErrorCode.Unknown;
  }

  private static _message(error: unknown): string {
    if (!isObject(error)) {
      return fallbackMessage;
    }
    const message = error['message'];
    return isString(message) ? message : fallbackMessage;
  }
}

/**
 * Calls the AI Receptionist callables that do paid AI work on the server, so
 * the editor never holds a vendor key. Every failure reaches the caller as an
 * `AiReceptionistCallableError`.
 */
@Injectable({ providedIn: 'root' })
export class AiReceptionistAiCallableService {
  /**
   * Reads the practice website, or reuses its stored Website Snapshot when
   * `useSnapshot` is set, into an Import Proposal for review. A read that
   * yields nothing usable resolves with `ImportFromWebsiteStatus.Empty` and
   * the reason.
   */
  async importFromWebsite(
    request: IImportFromWebsiteRequest
  ): Promise<IImportFromWebsiteResponse> {
    return this._call<IImportFromWebsiteRequest, IImportFromWebsiteResponse>(
      'http-aiReceptionist-importFromWebsite',
      request,
      importFromWebsiteTimeoutMs
    );
  }

  /**
   * Writes the Our Team overview from the practice's clinician roster. The
   * server owns the prompt and the model, so the browser only sends facts.
   */
  async generateTeamOverview(
    request: IGenerateTeamOverviewRequest
  ): Promise<IGenerateTeamOverviewResponse> {
    return this._call<
      IGenerateTeamOverviewRequest,
      IGenerateTeamOverviewResponse
    >(
      'http-aiReceptionist-generateTeamOverview',
      request,
      generateTeamOverviewTimeoutMs
    );
  }

  private async _call<RequestData, ResponseData>(
    functionName: string,
    request: RequestData,
    timeout: number
  ): Promise<ResponseData> {
    try {
      const callable = httpsCallable<
        SerialisedData<RequestData>,
        SerialisedData<ResponseData>
      >(functionName, { timeout });
      const serialised = await snapshot(callable(serialise(request)));
      return unserialise(serialised) as ResponseData;
    } catch (error) {
      throw AiReceptionistCallableError.from(error);
    }
  }
}
